package org.multiplatform.kotfolio.pages

import androidx.compose.runtime.*
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.core.Page

@Page("constraintlogin")
@Composable
fun ConstraintLogin() {
    BlogLayout(
        PageProperties(
            "constraint",
            ""
        ),
        BlogProperties("Kotlin Multiplatform", "Constraint")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            KotlinCode(
                code = """
                    @Composable
                    @Preview
                    fun App() {
                        PreComposeApp {
                            val navigator = rememberNavigator()
                            AppTheme {
                                NavHost(
                                    navigator = navigator,
                                    initialRoute = "/login",
                                ){
                                    scene("/login") {
                                        LoginScene(onRegisterClicked = {
                                            navigator.navigate("/register")
                                        })
                                    }
                                    scene("/register") {
                                        RegisterScene{ navigator.goBack() }
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    data class LoginViewState(
                        val isLoading: Boolean = false,
                        val isError:Boolean = false,
                        val message: String = "",
                        val user: User? = null
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    class LoginViewModel(
                        private val loginUseCase: ILoginUseCase,
                    ): ViewModel() {
                        private var  job: Job = Job()
                        private val _loginStateFlow = MutableStateFlow(LoginViewState())
                        val loginStateFlow: StateFlow<LoginViewState> = _loginStateFlow

                        fun doLogin(userName:String,password:String) {
                            job.cancel()
                            _loginStateFlow.update { it.copy(isLoading = true) }
                            job =  viewModelScope.launch {
                                       loginUseCase.invoke(userName,password)
                                   .fold(
                                       {_loginStateFlow.update { state-> state.copy(isLoading = false, user = it) } },
                                       {_loginStateFlow.update {stat -> stat.copy(isLoading = false,isError = true, message = it.message?:"") } }
                                   )
                           }
                        }

                        fun errorMessageDisplayed(){
                            _loginStateFlow.update { it.copy(isLoading = false, message = "", isError = false) }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun LoginScene(viewModel: LoginViewModel = koinViewModel(LoginViewModel::class), onRegisterClicked:() -> Unit) {
                        var userName by remember { mutableStateOf("") }
                        var password by remember { mutableStateOf("") }
                        var rememberMe by remember { mutableStateOf(false) }
                        val scope = rememberCoroutineScope()
                        val snackBarHostState = remember { SnackbarHostState() }

                        val state by viewModel.loginStateFlow.collectAsStateWithLifecycle(scope.coroutineContext)

                        LaunchedEffect(state) {
                            if (state.isError && state.message.isNotEmpty()) {
                                scope.launch {
                                    snackBarHostState.showSnackbar(state.message)
                                }
                                viewModel.errorMessageDisplayed()
                            }
                        }

                        Scaffold(snackbarHost = { SnackbarHost(hostState = snackBarHostState) }) { contentPadding ->
                            ConstraintLayout(
                                modifier = Modifier
                                    .background(AppTheme.colors.background)
                                    .scrollable(rememberScrollState(), Orientation.Vertical)
                                    .fillMaxWidth()
                                    .fillMaxHeight()
                                    .padding(15.dp)
                            ) {
                                val (refLogoIcon, refLogoText, refUsername, refPassword, refSwitch, refSwitchText, refLoginBtn, refRegisterText, refRegisterAction, refLoader) = createRefs()
                                val topGuideline = createGuidelineFromTop(0.40f)

                                Icon(
                                    Icons.Default.Lock,
                                    contentDescription = "...",
                                    tint = AppTheme.colors.secondary,
                                    modifier = Modifier
                                        .size(100.dp)
                                        .constrainAs(refLogoIcon) {
                                            bottom.linkTo(refLogoText.top, 10.dp)
                                            end.linkTo(refLogoText.end)
                                            start.linkTo(refLogoText.start)
                                        }
                                )

                                Text(
                                    text = "LOGIN",
                                    style = AppTheme.typography.label
                                        .copy(
                                            fontSize = 25.sp,
                                            letterSpacing = 5.sp,
                                            color = AppTheme.colors.text
                                        ),
                                    modifier = Modifier.constrainAs(refLogoText) {
                                        end.linkTo(parent.end)
                                        start.linkTo(parent.start)
                                        bottom.linkTo(topGuideline, 80.dp)
                                    }
                                )

                                OutlinedTextField(
                                    modifier = Modifier
                                        .testTag("userName")
                                        .fillMaxWidth()
                                        .border(
                                            width = 1.dp,
                                            color = AppTheme.colors.secondary,
                                            shape = RoundedCornerShape(25.dp)
                                        )
                                        .constrainAs(refUsername) {
                                            top.linkTo(topGuideline)
                                            start.linkTo(parent.start)
                                            end.linkTo(parent.end)
                                        },
                                    maxLines = 1,
                                    value = userName,
                                    onValueChange = { userName = it },
                                    placeholder = {
                                        Text(
                                            "Enter User Name",
                                            style = AppTheme.typography.body.copy(color = AppTheme.colors.text)
                                        )
                                    },
                                    colors = OutlinedTextFieldDefaults.colors(
                                        focusedBorderColor = Color.Transparent,
                                        unfocusedBorderColor = Color.Transparent,
                                        focusedLabelColor = AppTheme.colors.secondary,
                                        cursorColor = AppTheme.colors.secondary
                                    ),
                                    textStyle = AppTheme.typography.body.copy(color = AppTheme.colors.text)
                                )

                                OutlinedTextField(
                                    modifier = Modifier
                                        .testTag("password")
                                        .fillMaxWidth()
                                        .border(
                                            width = 1.dp,
                                            color = AppTheme.colors.secondary,
                                            shape = RoundedCornerShape(25.dp)
                                        )
                                        .constrainAs(refPassword) {
                                            top.linkTo(refUsername.bottom, 10.dp)
                                            start.linkTo(parent.start)
                                            end.linkTo(parent.end)
                                        },
                                    maxLines = 1,
                                    value = password,
                                    onValueChange = { password = it },
                                    placeholder = {
                                        Text(
                                            "Enter Password",
                                            style = AppTheme.typography.body.copy(color = AppTheme.colors.text)
                                        )
                                    },
                                    visualTransformation = PasswordVisualTransformation(),
                                    colors = OutlinedTextFieldDefaults.colors(
                                        focusedBorderColor = Color.Transparent,
                                        unfocusedBorderColor = Color.Transparent,
                                        focusedLabelColor = AppTheme.colors.secondary,
                                        cursorColor = AppTheme.colors.secondary
                                    ),
                                    textStyle = AppTheme.typography.body.copy(color = AppTheme.colors.text)
                                )

                                Switch(
                                    checked = rememberMe,
                                    modifier = Modifier
                                        .padding(start = 2.dp, top = 5.dp)
                                        .constrainAs(refSwitch) {
                                            top.linkTo(refPassword.bottom, 10.dp)
                                            start.linkTo(refPassword.start, 10.dp)
                                        },
                                    onCheckedChange = { rememberMe = it },
                                    colors = SwitchDefaults.colors(
                                        uncheckedTrackColor = Color.Transparent,
                                        uncheckedThumbColor = AppTheme.colors.primary,
                                        uncheckedBorderColor = AppTheme.colors.secondary,
                                        checkedThumbColor = AppTheme.colors.secondary,
                                        checkedTrackColor = AppTheme.colors.primary,
                                        checkedBorderColor = AppTheme.colors.secondary
                                    )
                                )
                                Text(
                                    text = "Remember me",
                                    style = AppTheme.typography.label.copy(AppTheme.colors.text),
                                    modifier = Modifier
                                        .padding(start = 15.dp, end = 5.dp)
                                        .constrainAs(refSwitchText) {
                                            start.linkTo(refSwitch.end)
                                            top.linkTo(refSwitch.top)
                                            bottom.linkTo(refSwitch.bottom)
                                        }
                                )

                                Button(
                                    modifier = Modifier
                                        .testTag("doLogin")
                                        .fillMaxWidth(0.5f)
                                        .constrainAs(refLoginBtn) {
                                            top.linkTo(refSwitch.bottom, 30.dp)
                                            start.linkTo(parent.start)
                                            end.linkTo(parent.end)
                                        },
                                    colors = ButtonDefaults.buttonColors(containerColor = AppTheme.colors.secondary),
                                    onClick = {
                                        viewModel.doLogin(userName, password)
                                    }

                                ) {
                                    Text(
                                        text = "Login",
                                        style = AppTheme.typography.label.copy(color = AppTheme.colors.text)
                                    )
                                }
                                if (state.isLoading)
                                    CircularProgressIndicator(
                                        modifier = Modifier.width(25.dp).constrainAs(refLoader) {
                                            top.linkTo(refLoginBtn.bottom, 10.dp)
                                            start.linkTo(refLoginBtn.start)
                                            end.linkTo(refLoginBtn.end)
                                        },
                                        color = MaterialTheme.colorScheme.secondary,
                                        trackColor = MaterialTheme.colorScheme.surfaceVariant,
                                        strokeWidth = 2.dp
                                    )

                                Text("Click here to register",
                                    style = AppTheme.typography.body.copy(color = AppTheme.colors.text),
                                    modifier = Modifier
                                        .constrainAs(refRegisterText) {
                                            bottom.linkTo(parent.bottom, 5.dp)
                                            start.linkTo(parent.start)
                                            end.linkTo(parent.end)
                                        }
                                        .clickable {
                                            scope.launch {
                                                onRegisterClicked()
                                            }

                                        })
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @OptIn(ExperimentalResourceApi::class)
                    @Composable
                    fun RegisterScene(onCancel: () -> Unit) {
                        var userName by remember { mutableStateOf("") }
                        var email by remember { mutableStateOf("") }
                        var password by remember { mutableStateOf("") }
                        var confirmPassword by remember { mutableStateOf("") }
                        val scope = rememberCoroutineScope()
                        val snackBarHostState = remember { SnackbarHostState() }
                        val premiumNonPremiumOptions = listOf("Premium", "Non Premium")
                        val (selectedOption, setSelectedOption) = remember { mutableStateOf(premiumNonPremiumOptions[0]) }

                        val genderOptions = listOf("Select Gender", "Male", "Female", "Prefer Not To Reveal")
                        var selectedItem by remember { mutableStateOf(genderOptions[0]) }
                        var expanded by remember { mutableStateOf(false) }
                        var showDialog by remember { mutableStateOf(false) }

                        val factory: PermissionsControllerFactory = rememberPermissionsControllerFactory()
                        val controller: PermissionsController =
                            remember(factory) { factory.createPermissionsController() }
                        val mediaFactory = rememberMediaPickerControllerFactory()
                        val mediaPicker = remember(mediaFactory) { mediaFactory.createMediaPickerController() }
                        val placeholder = imageResource(Res.drawable.placeholder)
                        var profileImage by remember { mutableStateOf(placeholder) }
                        BindEffect(controller)
                        BindMediaPickerEffect(mediaPicker)

                        Scaffold(snackbarHost = { SnackbarHost(hostState = snackBarHostState) }) { _ ->
                            ConstraintLayout(
                                modifier = Modifier
                                    .background(AppTheme.colors.background)
                                    .scrollable(rememberScrollState(), Orientation.Vertical)
                                    .fillMaxWidth()
                                    .fillMaxHeight()
                                    .padding(15.dp)
                            ) {

                                val (refLogoIcon,
                                    refLogoText,
                                    refName,
                                    refPassword,
                                    refConfirmPassword,
                                    refLoginBtn,
                                    refRegisterText,
                                    refRegisterAction,
                                    refLoader,
                                    refEmail,
                                    refPremiumNonPremiumOption,
                                    refGenderOptions
                                ) = createRefs()
                                val topGuideline = createGuidelineFromTop(0.35f)

                                Box(modifier = Modifier.constrainAs(refLogoIcon) {
                                    bottom.linkTo(refLogoText.top, 5.dp)
                                    end.linkTo(parent.end)
                                    start.linkTo(parent.start)
                                    horizontalBias = 0.522f
                                }.size(130.dp)) {
                                    Image(
                                        bitmap = profileImage,
                                        //  bitmap = profileImage!!,
                                        contentDescription = "...",
                                        contentScale = ContentScale.Crop,
                                        modifier = Modifier
                                            .border(width = 1.dp, color = Color.Black, shape = RoundedCornerShape(10))
                                            .size(120.dp)
                                            .clip(RoundedCornerShape(percent = 10))
                                    )
                                    Icon(
                                        Icons.Default.Edit,
                                        contentDescription = "...",
                                       // tint = AppTheme.colors.secondary,
                                        modifier = Modifier
                                            .background(Color.White, CircleShape)
                                            .padding(3.dp)
                                            .align(Alignment.CenterEnd)
                                            .size(18.dp)
                                            .clickable {
                                                scope.launch {
                                                    try {
                                                        controller.providePermission(Permission.GALLERY)
                                                        // Permission has been granted successfully.
                                                        val result = mediaPicker.pickImage(MediaSource.GALLERY)
                                                        profileImage = result.toImageBitmap()
                                                    } catch (deniedAlways: DeniedAlwaysException) {
                                                        // Permission is always denied.
                                                        controller.openAppSettings()
                                                        snackBarHostState.showSnackbar(deniedAlways.message.toString())
                                                    } catch (denied: DeniedException) {
                                                        // Permission was denied.
                                                        snackBarHostState.showSnackbar(denied.message.toString())
                                                    }
                                                }
                                            }
                                    )
                                }

                                Text(
                                    text = "PHOTO",
                                    style = AppTheme.typography.label
                                        .copy(
                                            fontSize = 25.sp,
                                            letterSpacing = 5.sp,
                                            color = AppTheme.colors.text
                                        ),
                                    modifier = Modifier.constrainAs(refLogoText) {
                                        end.linkTo(parent.end)
                                        start.linkTo(parent.start)
                                        bottom.linkTo(topGuideline, 60.dp)
                                    }
                                )


                                Row(
                                    verticalAlignment = Alignment.CenterVertically,
                                    modifier = Modifier
                                        .testTag("premiumNonPremiumOption")
                                        .fillMaxWidth()
                                        .border(
                                            width = 1.dp,
                                            color = AppTheme.colors.secondary,
                                            shape = RoundedCornerShape(25.dp)
                                        ).padding(start = 5.dp)
                                        .constrainAs(refPremiumNonPremiumOption) {
                                            top.linkTo(topGuideline)
                                            start.linkTo(parent.start)
                                            end.linkTo(parent.end)
                                        }) {
                                    premiumNonPremiumOptions.forEach { option ->
                                        RadioButton(
                                            selected = option == selectedOption,
                                            onClick = { setSelectedOption(option) },
                                            colors = RadioButtonDefaults.colors(selectedColor = AppTheme.colors.primary) // Customize the selected color
                                        )
                                        Text(
                                            text = option,
                                            style = AppTheme.typography.body.copy(color = AppTheme.colors.text)
                                        )
                                    }
                                }

                                OutlinedTextField(
                                    modifier = Modifier
                                        .testTag("fullName")
                                        .fillMaxWidth()
                                        .border(
                                            width = 1.dp,
                                            color = AppTheme.colors.secondary,
                                            shape = RoundedCornerShape(25.dp)
                                        )
                                        .constrainAs(refName) {
                                            top.linkTo(refPremiumNonPremiumOption.bottom, 10.dp)
                                            start.linkTo(parent.start)
                                            end.linkTo(parent.end)
                                        },
                                    maxLines = 1,
                                    value = userName,
                                    onValueChange = { userName = it },
                                    placeholder = {
                                        Text(
                                            "Enter Full Name",
                                            style = AppTheme.typography.body.copy(color = AppTheme.colors.text)
                                        )
                                    },
                                    colors = OutlinedTextFieldDefaults.colors(
                                        focusedBorderColor = Color.Transparent,
                                        unfocusedBorderColor = Color.Transparent,
                                        focusedLabelColor = AppTheme.colors.secondary,
                                        cursorColor = AppTheme.colors.secondary
                                    ),
                                    textStyle = AppTheme.typography.body.copy(color = AppTheme.colors.text)
                                )

                                OutlinedTextField(
                                    modifier = Modifier
                                        .testTag("fullName")
                                        .fillMaxWidth()
                                        .border(
                                            width = 1.dp,
                                            color = AppTheme.colors.secondary,
                                            shape = RoundedCornerShape(25.dp)
                                        )
                                        .constrainAs(refEmail) {
                                            top.linkTo(refName.bottom, 10.dp)
                                            start.linkTo(parent.start)
                                            end.linkTo(parent.end)
                                        },
                                    maxLines = 1,
                                    value = email,
                                    onValueChange = { email = it },
                                    placeholder = {
                                        Text(
                                            "Enter Email",
                                            style = AppTheme.typography.body.copy(color = AppTheme.colors.text)
                                        )
                                    },
                                    colors = OutlinedTextFieldDefaults.colors(
                                        focusedBorderColor = Color.Transparent,
                                        unfocusedBorderColor = Color.Transparent,
                                        focusedLabelColor = AppTheme.colors.secondary,
                                        cursorColor = AppTheme.colors.secondary
                                    ),
                                    textStyle = AppTheme.typography.body.copy(color = AppTheme.colors.text)
                                )


                                Box(
                                    modifier = Modifier
                                        .testTag("genderContainer")
                                        .fillMaxWidth()
                                        .border(
                                            width = 1.dp,
                                            color = AppTheme.colors.secondary,
                                            shape = RoundedCornerShape(25.dp)
                                        )
                                        .constrainAs(refGenderOptions) {
                                            top.linkTo(refEmail.bottom, 10.dp)
                                            start.linkTo(parent.start)
                                            end.linkTo(parent.end)
                                        }
                                ) {
                                    Column {
                                        OutlinedTextField(
                                            value = selectedItem,
                                            onValueChange = { },
                                            modifier = Modifier.fillMaxWidth(),
                                            trailingIcon = { Icon(Icons.Outlined.ArrowDropDown, null) },
                                            readOnly = true,
                                            colors = OutlinedTextFieldDefaults.colors(
                                                focusedBorderColor = Color.Transparent,
                                                unfocusedBorderColor = Color.Transparent,
                                                focusedLabelColor = AppTheme.colors.secondary,
                                                cursorColor = AppTheme.colors.secondary,

                                                ),
                                            textStyle = AppTheme.typography.body.copy(
                                                color =
                                                AppTheme.colors.text
                                            )
                                        )

                                        DropdownMenu(
                                            expanded = expanded,
                                            onDismissRequest = { expanded = false },
                                            modifier = Modifier
                                                .fillMaxWidth()

                                        ) {
                                            genderOptions.forEach { item ->
                                                DropdownMenuItem(
                                                    onClick = {
                                                        selectedItem = item
                                                        expanded = false
                                                    }
                                                ) {
                                                    Text(
                                                        text = item,
                                                        style = AppTheme.typography.body.copy(color = AppTheme.colors.text)

                                                    )
                                                }
                                            }
                                        }
                                    }

                                    Spacer(
                                        modifier = Modifier
                                            .matchParentSize()
                                            .background(Color.Transparent)
                                            .padding(10.dp)
                                            .clickable(
                                                onClick = { expanded = !expanded }
                                            )
                                    )
                                }


                                OutlinedTextField(
                                    modifier = Modifier
                                        .testTag("password")
                                        .fillMaxWidth()
                                        .border(
                                            width = 1.dp,
                                            color = AppTheme.colors.secondary,
                                            shape = RoundedCornerShape(25.dp)
                                        )
                                        .constrainAs(refPassword) {
                                            top.linkTo(refGenderOptions.bottom, 10.dp)
                                            start.linkTo(parent.start)
                                            end.linkTo(parent.end)
                                        },
                                    maxLines = 1,
                                    value = password,
                                    onValueChange = { password = it },
                                    placeholder = {
                                        Text(
                                            "Enter Password",
                                            style = AppTheme.typography.body.copy(color = AppTheme.colors.text)
                                        )

                                    },
                                    visualTransformation = PasswordVisualTransformation(),
                                    colors = OutlinedTextFieldDefaults.colors(
                                        focusedBorderColor = Color.Transparent,
                                        unfocusedBorderColor = Color.Transparent,
                                        focusedLabelColor = AppTheme.colors.secondary,
                                        cursorColor = AppTheme.colors.secondary
                                    ),
                                    textStyle = AppTheme.typography.body.copy(color = AppTheme.colors.text)
                                )

                                OutlinedTextField(
                                    modifier = Modifier
                                        .testTag("confirmPassword")
                                        .fillMaxWidth()
                                        .border(
                                            width = 1.dp,
                                            color = AppTheme.colors.secondary,
                                            shape = RoundedCornerShape(25.dp)
                                        )
                                        .constrainAs(refConfirmPassword) {
                                            top.linkTo(refPassword.bottom, 10.dp)
                                            start.linkTo(parent.start)
                                            end.linkTo(parent.end)
                                        },
                                    maxLines = 1,
                                    value = confirmPassword,
                                    onValueChange = { confirmPassword = it },
                                    placeholder = {
                                        Text(
                                            "Confirm Password",
                                            style = AppTheme.typography.body.copy(color = AppTheme.colors.text)
                                        )

                                    },
                                    visualTransformation = PasswordVisualTransformation(),
                                    colors = OutlinedTextFieldDefaults.colors(
                                        focusedBorderColor = Color.Transparent,
                                        unfocusedBorderColor = Color.Transparent,
                                        focusedLabelColor = AppTheme.colors.secondary,
                                        cursorColor = AppTheme.colors.secondary
                                    ),
                                    textStyle = AppTheme.typography.body.copy(color = AppTheme.colors.text)

                                )

                                Row(
                                    horizontalArrangement = Arrangement.Center,
                                    modifier = Modifier
                                        .testTag("doRegister")
                                        .fillMaxWidth()
                                        .constrainAs(refRegisterAction) {
                                            top.linkTo(refConfirmPassword.bottom, 30.dp)
                                            start.linkTo(parent.start)
                                            end.linkTo(parent.end)
                                        }
                                        .padding(start = 12.dp, end = 12.dp)
                                ) {
                                    Button(
                                        modifier = Modifier
                                            .padding(5.dp)
                                            .weight(1f)
                                            .testTag("doRegister"),
                                        colors = ButtonDefaults.buttonColors(containerColor = AppTheme.colors.secondary),
                                        onClick = {
                                        }

                                    ) {
                                        Text(
                                            text = "Register",
                                            style = AppTheme.typography.label.copy(color = AppTheme.colors.text)
                                        )
                                    }

                                    Button(
                                        modifier = Modifier
                                            .padding(5.dp)
                                            .weight(1f)
                                            .testTag("doCancel"),
                                        colors = ButtonDefaults.buttonColors(containerColor = AppTheme.colors.secondary),
                                        onClick = {
                                            showDialog = true
                                        }

                                    ) {
                                        Text(
                                            text = "Cancel",
                                            style = AppTheme.typography.label.copy(color = AppTheme.colors.text)
                                        )
                                    }
                                }

                                if (showDialog)
                                    AlertDialog(
                                        text = {
                                            Text(
                                                "Are You Sure To Cancel?",
                                                style = AppTheme.typography.label.copy(color = AppTheme.colors.text)
                                            )
                                        },
                                        onDismissRequest = { showDialog = false },
                                        confirmButton = {
                                            Button(
                                                modifier = Modifier
                                                    .padding(5.dp)
                                                    .testTag("doCancel"),
                                                colors = ButtonDefaults.buttonColors(containerColor = AppTheme.colors.secondary),

                                                onClick = {
                                                    showDialog = false
                                                    onCancel()
                                                }
                                            ) {
                                                Text(
                                                    "Confirm",
                                                    style = AppTheme.typography.body.copy(color = AppTheme.colors.text)
                                                )
                                            }
                                        },
                                        dismissButton = {
                                            Button(
                                                modifier = Modifier
                                                    .padding(5.dp)
                                                    .testTag("doCancel"),
                                                colors = ButtonDefaults.buttonColors(containerColor = AppTheme.colors.secondary),
                                                onClick = {
                                                    showDialog = false
                                                }
                                            ) {
                                                Text(
                                                    "Dismiss",
                                                    style = AppTheme.typography.body.copy(color = AppTheme.colors.text)
                                                )
                                            }
                                        },
                                    )
                            }
                        }
                    }
                        
                    """.trimIndent()
            )
        }
    }
}


